/**
 * Utility helpers for Germany map rendering: normalization, geography slicing,
 * bounds calculation, and layer availability for the regional filter map.
 * Stays UI-agnostic: only deals with data inspection and normalization.
 * Author: bb
 */

import { BUNDESLAENDER } from "@/services/mockData";
import POSTAL_TO_STATE_RAW from "@/data/map/postalCodeToFederalState.json";
import POSTAL_TO_NAME from "@/data/map/postalCodeToName.json";

import type {
  Coordinates,
  Latitude,
  Longitude,
} from "@vnedyalk0v/react19-simple-maps";

// Keep lightweight URLs for runtime-loaded topojson assets.
export const FEDERAL_STATES_TOPOJSON_URL = new URL(
  "@/data/map/germany-federal-states.topojson",
  import.meta.url
).href;
export const ADMINISTRATIVE_DISTRICTS_TOPOJSON_URL = new URL(
  "@/data/map/germany-administrative-districts.topojson",
  import.meta.url
).href;
export const DISTRICTS_TOPOJSON_URL = new URL(
  "@/data/map/germany-districts.topojson",
  import.meta.url
).href;
export const POSTAL_CODES_COARSE_TOPOJSON_URL = new URL(
  "@/data/map/lod/germany-postal-codes-coarse.topojson",
  import.meta.url
).href;
export const POSTAL_CODES_MEDIUM_TOPOJSON_URL = new URL(
  "@/data/map/lod/germany-postal-codes-medium.topojson",
  import.meta.url
).href;
export const POSTAL_CODES_FULL_TOPOJSON_URL = new URL(
  "@/data/map/lod/germany-postal-codes-full.topojson",
  import.meta.url
).href;

// ISO-like identifiers used by some Bundesland datasets.
const FEDERAL_STATE_ISO_LOOKUP: Record<string, string> = {
  "de-bw": "Baden-Württemberg",
  "de-by": "Bayern",
  "de-be": "Berlin",
  "de-bb": "Brandenburg",
  "de-hb": "Bremen",
  "de-hh": "Hamburg",
  "de-he": "Hessen",
  "de-mv": "Mecklenburg-Vorpommern",
  "de-ni": "Niedersachsen",
  "de-nw": "Nordrhein-Westfalen",
  "de-rp": "Rheinland-Pfalz",
  "de-sl": "Saarland",
  "de-sn": "Sachsen",
  "de-st": "Sachsen-Anhalt",
  "de-sh": "Schleswig-Holstein",
  "de-th": "Thüringen",
};

const lon = (n: number) => n as unknown as Longitude;
const lat = (n: number) => n as unknown as Latitude;
export const coords = (x: number, y: number): Coordinates => [lon(x), lat(y)];

const POSTAL_TO_STATE = POSTAL_TO_STATE_RAW as Record<string, string | null>;

const buildPostalCodeDisplayLookup = (): Record<string, string> => {
  const lookup: Record<string, string> = {};
  Object.entries(POSTAL_TO_NAME).forEach(([plz, area]) => {
    const name = typeof area === "string" && area.trim() ? area.trim() : null;
    lookup[plz] = name ? `${plz} – ${name}` : plz;
  });
  return lookup;
};

const POSTAL_CODE_DISPLAY_LOOKUP = buildPostalCodeDisplayLookup();

/** Resolve a postal code to its enriched display label (PLZ plus locality). */
export function getPostalCodeDisplayLabel(
  plz: string | number | null | undefined
): string | null {
  if (plz == null) return null;
  const raw = String(plz);
  const normalized = raw.split("–")[0]?.trim().split(/\s+/)[0] || raw;
  const lookupKey = normalized || raw;
  const match =
    POSTAL_CODE_DISPLAY_LOOKUP[lookupKey] ?? POSTAL_CODE_DISPLAY_LOOKUP[raw];
  if (match) return match;
  return raw;
}

export interface GeoBounds {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}

const createEmptyBounds = (): GeoBounds => ({
  minLon: Infinity,
  minLat: Infinity,
  maxLon: -Infinity,
  maxLat: -Infinity,
});

const isBoundsValid = (bounds: GeoBounds) =>
  Number.isFinite(bounds.minLon) &&
  Number.isFinite(bounds.minLat) &&
  Number.isFinite(bounds.maxLon) &&
  Number.isFinite(bounds.maxLat);

const extendWithCoordinate = (bounds: GeoBounds, coord: any) => {
  const [lon, lat] = coord as [number, number];
  if (lon < bounds.minLon) bounds.minLon = lon;
  if (lon > bounds.maxLon) bounds.maxLon = lon;
  if (lat < bounds.minLat) bounds.minLat = lat;
  if (lat > bounds.maxLat) bounds.maxLat = lat;
};

const traverseCoordinates = (bounds: GeoBounds, coords: any): void => {
  if (!Array.isArray(coords)) return;
  if (typeof coords[0] === "number") {
    extendWithCoordinate(bounds, coords);
    return;
  }
  coords.forEach((sub) => traverseCoordinates(bounds, sub));
};

// Optional dev logging hooks for debugging the map data flow.
const isDevEnvironment =
  typeof import.meta !== "undefined" ? import.meta.env?.DEV ?? false : false;
// Flip to true when you need to inspect map helper output locally.
const mapDebugEnabled = false;

const mapHelpersLog = (...args: any[]) => {
  if (!isDevEnvironment || !mapDebugEnabled) return;
  console.log("[MapHelpers]", ...args);
};

let postalCodeDebugLogged = false;

/**
 * Compute a bounding box for a single GeoJSON feature.
 * Returns null when no coordinates are present.
 */
export function getFeatureBounds(feature: any): GeoBounds | null {
  if (!feature?.geometry?.coordinates) return null;
  const bounds = createEmptyBounds();
  traverseCoordinates(bounds, feature.geometry.coordinates);
  return isBoundsValid(bounds) ? bounds : null;
}

/**
 * Compute the combined bounds of a GeoJSON FeatureCollection.
 * Ignores malformed entries and returns null if nothing usable is found.
 */
export function getGeographyBounds(geography: any): GeoBounds | null {
  const features = geography?.features;
  if (!Array.isArray(features) || features.length === 0) return null;
  const bounds = createEmptyBounds();
  features.forEach((feature) => {
    const featureBounds = getFeatureBounds(feature);
    if (!featureBounds) return;
    if (featureBounds.minLon < bounds.minLon)
      bounds.minLon = featureBounds.minLon;
    if (featureBounds.minLat < bounds.minLat)
      bounds.minLat = featureBounds.minLat;
    if (featureBounds.maxLon > bounds.maxLon)
      bounds.maxLon = featureBounds.maxLon;
    if (featureBounds.maxLat > bounds.maxLat)
      bounds.maxLat = featureBounds.maxLat;
  });
  return isBoundsValid(bounds) ? bounds : null;
}

// Germany bounding box (computed once via computeBounds.mjs)
export const GERMANY_BOUNDS: GeoBounds = {
  minLon: 5.87162,
  minLat: 47.26986,
  maxLon: 15.03811,
  maxLat: 55.05653,
};

// Normalization helpers keep state lookups resilient to spelling/diacritics.
const repairMojibake = (input: string | null | undefined): string | null => {
  if (input == null) {
    return null;
  }
  if (!input.includes("Ã") && !input.includes("â") && !input.includes("Â")) {
    return input;
  }
  try {
    const bytes = Uint8Array.from(Array.from(input), (ch) => ch.charCodeAt(0));
    return new TextDecoder("utf-8").decode(bytes);
  } catch {
    return input;
  }
};

const stripDiacritics = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const canonicalizeBundeslandKey = (
  value: string | number | null | undefined
) => {
  if (value == null) return "";
  const repaired = repairMojibake(String(value)) ?? String(value);
  let normalized = stripDiacritics(repaired.toLowerCase().trim());
  normalized = normalized.replace(/ß/g, "ss");
  normalized = normalized
    .replace(/ae/g, "a")
    .replace(/oe/g, "o")
    .replace(/ue/g, "u");
  normalized = normalized.replace(/[^a-z0-9]+/g, "");
  return normalized;
};

const ADDITIONAL_STATE_ALIASES: Array<
  [string, (typeof BUNDESLAENDER)[number]]
> = [
  ["baden wurttemberg", "Baden-Württemberg"],
  ["baden wurtemberg", "Baden-Württemberg"],
  ["baden-wurttemberg", "Baden-Württemberg"],
  ["baden-wurtemberg", "Baden-Württemberg"],
  ["baden wuerttemberg", "Baden-Württemberg"],
  ["baden-wuerttemberg", "Baden-Württemberg"],
  ["bavaria", "Bayern"],
  ["hesse", "Hessen"],
  ["lower saxony", "Niedersachsen"],
  ["north rhine westphalia", "Nordrhein-Westfalen"],
  ["north rhineland westphalia", "Nordrhein-Westfalen"],
  ["rhineland palatinate", "Rheinland-Pfalz"],
  ["saxony anhalt", "Sachsen-Anhalt"],
  ["saxony", "Sachsen"],
  ["schleswig holstein", "Schleswig-Holstein"],
  ["thuringia", "Thüringen"],
  ["mecklenburg western pomerania", "Mecklenburg-Vorpommern"],
  ["free hanseatic city of bremen", "Bremen"],
  ["freie hansestadt bremen", "Bremen"],
  ["free and hanseatic city of hamburg", "Hamburg"],
  ["freie und hansestadt hamburg", "Hamburg"],
];

const BASE_STATE_LOOKUP: Record<string, string> = BUNDESLAENDER.reduce(
  (acc, state) => {
    const key = canonicalizeBundeslandKey(state);
    if (key) acc[key] = state;
    return acc;
  },
  {} as Record<string, string>
);

const registerCanonicalName = (
  lookup: Record<string, string>,
  raw: string | null | undefined,
  canonical?: string | null
) => {
  const key = canonicalizeBundeslandKey(raw);
  if (!key) return;
  const resolved = canonical ?? BASE_STATE_LOOKUP[key];
  if (!resolved) return;
  lookup[key] = resolved;
};

const STATE_NORMALIZATION_LOOKUP: Record<string, string> = (() => {
  const lookup: Record<string, string> = { ...BASE_STATE_LOOKUP };

  ADDITIONAL_STATE_ALIASES.forEach(([alias, canonical]) =>
    registerCanonicalName(lookup, alias, canonical)
  );

  return lookup;
})();

/**
 * Resolve a variety of state spellings/aliases to a canonical Bundesland name.
 * Falls back to trimmed input when no match is found.
 */
export function normalizeBundeslandName(
  raw: string | number | null | undefined
): string | null {
  const key = canonicalizeBundeslandKey(raw);
  if (!key) return null;
  const resolved = STATE_NORMALIZATION_LOOKUP[key];
  if (resolved) return resolved;
  const fallback = BUNDESLAENDER.find(
    (state) => canonicalizeBundeslandKey(state) === key
  );
  if (fallback) {
    STATE_NORMALIZATION_LOOKUP[key] = fallback;
    return fallback;
  }
  return typeof raw === "string" ? raw.trim() : null;
}

export type MapLayerId =
  | "federalStates"
  | "administrativeDistricts"
  | "districts"
  | "postalCodes";

const FEDERAL_STATE_LAYER = {
  id: "federalStates" as const,
  geography: null,
};

const ADMINISTRATIVE_DISTRICTS_LAYER = {
  id: "administrativeDistricts" as const,
  geography: null,
};

const DISTRICTS_LAYER = {
  id: "districts" as const,
  geography: null,
};

/**
 * Postal-code layer for Germany (Postleitzahlen).
 * Rendered as an additional selectable detail layer.
 */
const POSTAL_CODES_LAYER = {
  id: "postalCodes" as const,
  geography: null,
};

export const MAP_LAYERS: Record<
  MapLayerId,
  { id: MapLayerId; geography: any }
> = {
  federalStates: FEDERAL_STATE_LAYER,
  administrativeDistricts: ADMINISTRATIVE_DISTRICTS_LAYER,
  districts: DISTRICTS_LAYER,
  postalCodes: POSTAL_CODES_LAYER,
};

// States with Regierungsbezirke (administrative districts); canonical keys only.
const ADMINISTRATIVE_DISTRICT_STATE_KEYS = new Set([
  "bayern",
  "badenwurttemberg",
  "hessen",
  "nordrheinwestfalen",
  "niedersachsen",
]);

const DISTRICT_UNAVAILABLE_STATE_KEYS = new Set(
  ["Berlin", "Hamburg", "Bremen"].map((state) =>
    canonicalizeBundeslandKey(state)
  )
);

export interface LayerGeographies {
  federalStates: any | null;
  administrativeDistricts: any | null;
  districts: any | null;
  postalCodes: any | null;
  postalCodesCoarse?: any | null;
  postalCodesMedium?: any | null;
  postalCodesFull?: any | null;
}

const normalizeParentCandidate = (
  ...candidates: Array<string | null | undefined>
): string | null => {
  for (const candidate of candidates) {
    const normalized = normalizeBundeslandName(candidate);
    if (normalized) return normalized;
  }
  for (const candidate of candidates) {
    if (typeof candidate === "string") {
      const trimmed = candidate.trim();
      if (trimmed) return trimmed;
    }
  }
  return null;
};

/** Extract the owning state for an administrative district feature. */
export function getAdministrativeDistrictParentState(
  feature: any
): string | null {
  const props = feature?.properties ?? {};
  return normalizeParentCandidate(
    props.NAME_1,
    props.name,
    props.state_name,
    props.admin
  );
}

/** Extract the owning state for a district feature. */
export function getDistrictParentState(feature: any): string | null {
  const props = feature?.properties ?? {};
  return normalizeParentCandidate(
    props.NAME_1,
    props.name,
    props.state_name,
    props.admin
  );
}

/**
 * Pick a display-friendly name for a feature based on layer type.
 * Handles varying property keys between the GeoJSON files.
 */
export function getFeatureDisplayName(
  layerId: MapLayerId,
  feature: any
): string | null {
  const props = feature?.properties ?? {};
  if (layerId === "federalStates") {
    return (
      normalizeBundeslandName(
        props.name ??
          props.NAME_1 ??
          props.NAME ??
          props.state_name ??
          props.admin
      ) || null
    );
  }

  if (layerId === "administrativeDistricts") {
    return repairMojibake(
      props.NAME_2 ||
      props.GEN ||
      props.name ||
      props.VARNAME_2 ||
      props.NL_NAME_2 ||
      null
    );
  }

  // Postal-code display: combine PLZ with the locality name when available.
  if (layerId === "postalCodes") {
    const plz =
      props.postcode ||
      props.postal_code ||
      props.PLZ ||
      null;

    const areaName =
      props.note ||
      props.GEN ||
      props.name ||
      props.VARNAME_2 ||
      props.NL_NAME_2 ||
      null;

    if (!plz) return null;

    const normalizedAreaName = areaName ? repairMojibake(String(areaName)) : null;
    return normalizedAreaName ? `${plz} – ${normalizedAreaName}` : String(plz);
  }

  return repairMojibake(
    props.NAME_3 ||
    props.GEN ||
    props.name ||
    props.VARNAME_3 ||
    props.NL_NAME_3 ||
    null
  );
}

/**
 * Return the parent state for a feature, adapting to layer-specific properties.
 * Falls back to feature id when needed for federal state layer.
 */
export function getFeatureParentState(
  layerId: MapLayerId,
  feature: any
): string | null {
  // We derive parent information differently per layer because the GeoJSON varies.
  if (layerId === "federalStates") {
    const props = feature?.properties ?? {};
    const isoCandidate = props.id ?? props.ISO ?? props.iso ?? null;
    const isoMatch =
      typeof isoCandidate === "string"
        ? FEDERAL_STATE_ISO_LOOKUP[isoCandidate.toLowerCase()]
        : null;
    return (
      normalizeParentCandidate(
        props.name,
        props.NAME_1,
        props.NAME,
        isoMatch,
        feature?.id
      ) ?? isoMatch
    );
  }

  if (layerId === "administrativeDistricts") {
    return getAdministrativeDistrictParentState(feature);
  }

  if (layerId === "postalCodes") {
    // Postal-code areas: lookup parent Bundesland via PLZ→state mapping.
    // Falls back to null when PLZ is missing or unmapped.
    const plz =
      feature?.properties?.postcode ||
      feature?.properties?.postal_code ||
      feature?.properties?.PLZ ||
      null;

    if (!plz) return null;

    const state = POSTAL_TO_STATE[String(plz)] || null;
    return normalizeBundeslandName(state);
  }

  return getDistrictParentState(feature);
}

/**
 * Build a stable selection key to track a region across layers and UI selections.
 * Keys follow the pattern "<layerId>:<state>:<regionName>".
 * Accepts either a GeoJSON feature or a direct region name for convenience.
 */
export function buildRegionSelectionKey(
  layerId: MapLayerId,
  featureOrName: any,
  parentState: string | null
): string {
  const featureName =
    typeof featureOrName === "string"
      ? featureOrName
      : getFeatureDisplayName(layerId, featureOrName) || "region";
  const normalizedFeatureName =
    layerId === "postalCodes"
      ? normalizePostalCodeKey(featureOrName, featureName)
      : featureName;
  const statePart = parentState || "unknown";
  return `${layerId}:${statePart}:${normalizedFeatureName}`;
}

const normalizePostalCodeKey = (
  featureOrName: any,
  fallback: string
): string => {
  if (featureOrName && typeof featureOrName === "object") {
    const props = featureOrName?.properties ?? {};
    const raw =
      props.postcode ||
      props.postal_code ||
      props.PLZ ||
      null;
    if (raw != null) {
      const normalized = normalizePostalCodeValue(String(raw));
      if (normalized) return normalized;
    }
  }

  const normalizedFallback = normalizePostalCodeValue(fallback);
  return normalizedFallback || fallback;
};

const normalizePostalCodeValue = (raw: string | null | undefined) => {
  if (!raw) return null;
  let digits = raw.replaceAll("[^0-9]", "");
  if (digits.length === 4) {
    digits = `0${digits}`;
  }
  return digits.length === 5 ? digits : null;
};

const MAP_LAYER_IDS: MapLayerId[] = [
  "federalStates",
  "administrativeDistricts",
  "districts",
  "postalCodes",
];

function isMapLayerId(value: string): value is MapLayerId {
  return MAP_LAYER_IDS.includes(value as MapLayerId);
}

/**
 * Parse a region selection key into its parts.
 * Returns null for malformed input.
 */
export function parseRegionSelectionKey(
  key: string | null | undefined
): { layerId: MapLayerId; state: string; regionName: string } | null {
  if (!key || typeof key !== "string") return null;
  const parts = key.split(":");
  if (parts.length < 3) return null;
  const [layerIdPart, state, ...rest] = parts;
  if (!state || !isMapLayerId(layerIdPart)) return null;
  const regionName = rest.join(":") || state;
  return {
    layerId: layerIdPart as MapLayerId,
    state,
    regionName,
  };
}

/**
 * Filter GeoJSON for the selected parent state while preserving top-level shape.
 * - Normalizes the target state key
 * - Returns base data for federal layer (or filtered single state when provided)
 * - Returns an empty feature list for non-federal layers without a target state
 * - Filters by parent state using NAME_1/name/state_name/admin consistently
 */
export function getLayerGeography(
  layerId: MapLayerId,
  selectedState: string | null,
  layers: LayerGeographies
): any {
  // Filter GeoJSON for the selected parent while keeping shape of original object.
  const normalizedState =
    normalizeBundeslandName(selectedState) ?? selectedState ?? null;
  const targetKey = canonicalizeBundeslandKey(normalizedState);
  const base = layers?.[layerId];
  const empty = { type: "FeatureCollection", features: [] as any[] };
  if (!base) return empty;

  if (
    layerId === "postalCodes" &&
    isDevEnvironment &&
    mapDebugEnabled &&
    !postalCodeDebugLogged
  ) {
    // Dev-only: surface the first postal-code feature props to inspect parent keys.
    console.log(
      "[PLZ Debug] First feature properties:",
      base?.features?.[0]?.properties ?? null
    );
    postalCodeDebugLogged = true;
  }

  mapHelpersLog("getLayerGeography:start", {
    layerId,
    selectedState,
    normalizedState,
    targetKey,
    totalFeatures: base?.features?.length ?? 0,
  });

  if (layerId === "federalStates") {
    if (!targetKey) return base;
    const candidatesLog: Array<{
      iso: string | null;
      isoName: string | null;
      parent: string | null;
      keys: string[];
      featureName: string | null;
    }> = [];
    const features = (base?.features ?? []).filter((feature: any) => {
      const props = feature?.properties ?? {};
      const parent = getFeatureParentState("federalStates", feature);
      const iso = props.id ?? props.ID ?? props.iso ?? props.ISO ?? feature?.id;
      const isoName =
        typeof iso === "string"
          ? FEDERAL_STATE_ISO_LOOKUP[iso.toLowerCase()] ?? iso
          : iso;
      const featureName = getFeatureDisplayName("federalStates", feature);
      const candidates = [parent, isoName, featureName];
      const keys = candidates
        .filter(Boolean)
        .map((c) => canonicalizeBundeslandKey(c as string));
      candidatesLog.push({
        iso: typeof iso === "string" ? iso : null,
        isoName: typeof isoName === "string" ? isoName : null,
        parent: parent ?? null,
        featureName: featureName ?? null,
        keys,
      });
      return keys.includes(targetKey);
    });
    mapHelpersLog("getLayerGeography:federal-filter", {
      targetKey,
      matches: features.length,
      candidatesLog,
    });
    return { ...base, features };
  }

  if (!targetKey) {
    mapHelpersLog("getLayerGeography:no-target", {
      layerId,
      selectedState,
      normalizedState,
    });
    return { ...base, features: [] };
  }

  const baseFeatures = base?.features ?? [];
  const features = baseFeatures.filter((feature: any) => {
    const parent = getFeatureParentState(layerId, feature);
    if (!parent) return false;
    return canonicalizeBundeslandKey(parent) === targetKey;
  });
  mapHelpersLog("getLayerGeography:filtered", {
    layerId,
    selectedState,
    normalizedState,
    targetKey,
    featureCount: features.length,
    featureNames: features
      .map((feature: any) => getFeatureDisplayName(layerId, feature))
      .filter(Boolean),
  });
  return { ...base, features };
}

/**
 * Check if a state exposes administrative districts (Regierungsbezirke).
 * Only Bayern, Baden-Württemberg, Hessen, and Nordrhein-Westfalen return true.
 */
export function isAdministrativeDistrictAvailable(
  state: string | null
): boolean {
  if (!state) return false;
  const normalizedState = normalizeBundeslandName(state) ?? state;
  const key = canonicalizeBundeslandKey(normalizedState);
  if (!key) return false;
  const available = ADMINISTRATIVE_DISTRICT_STATE_KEYS.has(key);
  mapHelpersLog("isAdministrativeDistrictAvailable", {
    state,
    normalizedState,
    key,
    available,
  });
  return available;
}

/**
 * Check whether district-level data is available for a state.
 * Berlin, Hamburg, and Bremen do not expose districts.
 */
export function isDistrictAvailable(state: string | null): boolean {
  if (!state) return false;
  const normalizedState = normalizeBundeslandName(state) ?? state;
  const key = canonicalizeBundeslandKey(normalizedState);
  if (!key) return false;
  const unavailable = DISTRICT_UNAVAILABLE_STATE_KEYS.has(key);
  mapHelpersLog("isDistrictAvailable", {
    state,
    normalizedState,
    key,
    available: !unavailable,
  });
  if (unavailable) return false;
  return true;
}

/**
 * Check whether postal-code layer can be shown for a state. All Bundesländer
 * expose postal codes; normalization keeps the truthy check consistent.
 */
export function isPostalCodeAvailable(state: string | null): boolean {
  // All states have postal codes; normalization reused.
  return !!normalizeBundeslandName(state);
}
